"use client";

import { AnimatedElement } from "@/components/marketing/AnimatedSection";
import { useLang } from "@/contexts/language-context";
import { CAPITAL_PRODUCTS } from "@/lib/capital/eligibility";

/**
 * Las cinco opciones, con sus rangos y requisitos REALES.
 *
 * 🔴 Los montos y mínimos salen de `CAPITAL_PRODUCTS`, la misma fuente que usa
 * el panel. Si SBL cambia su matriz se corrige en un solo sitio y la página
 * pública no queda mintiendo — un rango obsoleto en marketing de crédito es
 * exactamente el tipo de claim que no se puede permitir.
 */
export default function CapitalProducts() {
  const { t } = useLang();

  const copy: Record<string, { name: string; use: string; funding: string; term: string }> = {
    working_capital: {
      name: t("Working capital", "Capital de trabajo"),
      use: t("Cover a cash gap, make payroll, buy inventory before a busy season.", "Tapar un bache de caja, cubrir nómina, comprar inventario antes de temporada."),
      funding: t("24–72 hours", "24–72 horas"),
      term: t("3 to 24 months", "3 a 24 meses"),
    },
    term_loan: {
      name: t("Term loan", "Préstamo a plazo"),
      use: t("Remodel, open a second location, buy your space.", "Remodelar, abrir otra sucursal, comprar el local."),
      funding: t("1 to 3 days", "1 a 3 días"),
      term: t("1 to 7 years", "1 a 7 años"),
    },
    line_of_credit: {
      name: t("Line of credit", "Línea de crédito"),
      use: t("Money on standby you draw only when you need it. For uneven months.", "Dinero disponible que tomas solo cuando lo necesitas. Para meses irregulares."),
      funding: t("1 to 3 days", "1 a 3 días"),
      term: t("6 to 24 months", "6 a 24 meses"),
    },
    equipment: {
      name: t("Equipment financing", "Financiamiento de equipo"),
      use: t("Ovens, walk-ins, hoods, delivery vehicles.", "Hornos, cavas, campanas, vehículos de reparto."),
      funding: t("3 to 5 days", "3 a 5 días"),
      term: t("2 to 5 years", "2 a 5 años"),
    },
    sba: {
      name: t("SBA loan", "Préstamo SBA"),
      use: t("Backed by the Small Business Administration: the best terms, the longest process.", "Respaldado por la Small Business Administration: las mejores condiciones, el proceso más largo."),
      funding: t("2 to 4 weeks", "2 a 4 semanas"),
      term: t("5 to 10 years", "5 a 10 años"),
    },
  };

  const money = (cents: number) => {
    const d = Math.round(cents / 100);
    if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d % 1_000_000 === 0 ? 0 : 1)}M`;
    return `$${Math.round(d / 1_000)}K`;
  };

  return (
    <section className="relative py-24 px-6" style={{ background: "#F7F5F1" }}>
      <div className="max-w-[1200px] mx-auto">
        <div className="max-w-[620px] mb-14">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("The options", "Las opciones")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">
            {t("Five doors. We tell you which ones open.", "Cinco puertas. Te decimos cuáles se abren.")}
          </h2>
        </div>

        <div className="space-y-3">
          {CAPITAL_PRODUCTS.map((p, i) => {
            const c = copy[p.id];
            return (
              <AnimatedElement
                key={p.id}
                delay={Math.min(i * 0.07, 0.3)}
                className="group relative bg-white rounded-2xl border border-black/[0.07] p-6 md:p-7 hover:border-redy-orange/30 transition-colors overflow-hidden"
              >
                <div
                  className="absolute left-0 top-0 bottom-0 w-[3px] opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: "linear-gradient(180deg,#D7263D,#F4722B)" }}
                  aria-hidden
                />
                <div className="grid md:grid-cols-[1.4fr_1fr_auto] gap-5 md:gap-8 md:items-center">
                  <div>
                    <h3 className="text-[19px] font-black text-redy-charcoal">{c.name}</h3>
                    <p className="text-[14px] text-redy-secondary leading-relaxed mt-1.5">{c.use}</p>
                  </div>

                  <div className="flex gap-7">
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-redy-secondary/60">
                        {t("Funding", "Fondeo")}
                      </p>
                      <p className="text-[14px] font-bold text-redy-charcoal mt-1">{c.funding}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-redy-secondary/60">
                        {t("Term", "Plazo")}
                      </p>
                      <p className="text-[14px] font-bold text-redy-charcoal mt-1">{c.term}</p>
                    </div>
                  </div>

                  <div className="md:text-right">
                    <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-redy-secondary/60">
                      {t("Range", "Rango")}
                    </p>
                    <p className="text-[22px] font-black text-redy-charcoal tracking-tight mt-1 whitespace-nowrap">
                      {money(p.amountMinCents)}
                      <span className="text-black/20 mx-1.5">—</span>
                      {money(p.amountMaxCents)}
                    </p>
                  </div>
                </div>
              </AnimatedElement>
            );
          })}
        </div>

        <p className="text-[12.5px] text-redy-secondary/70 leading-relaxed mt-7 max-w-[760px]">
          {t(
            "Amounts, terms and requirements are the ranges published by SmallBusinessLoans and are not an offer of credit. Every lender sets its own criteria and decides independently. Available in the United States only.",
            "Los montos, plazos y requisitos son los rangos publicados por SmallBusinessLoans y no constituyen una oferta de crédito. Cada prestamista fija sus propios criterios y decide de forma independiente. Disponible solo en Estados Unidos."
          )}
        </p>
      </div>
    </section>
  );
}
